// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    SegmentedControl,
    SpaceBetween,
} from '@cloudscape-design/components';
import {useFilterPersistence} from '../../Contexts/FilterPersistenceContext';
import {useExperimentsState} from '../../Contexts/ExperimentsContext';
import CustomFiltersModal from './CustomFiltersModal';

/**
 * FilterControlsUI component
 * Pure UI component for filtering controls with three modes:
 * - All: Show all resources
 * - Hide Auto-Generated: Filter out auto-generated resources
 * - Custom Filters: Apply custom keyword-based filters
 * 
 * Only visible when experiments toggle is enabled
 */
function FilterControlsUI() {
    const {experimentsEnabled} = useExperimentsState();
    const {
        viewMode,
        excludePrefixes,
        keywordFilter,
        hideAutoGenerated,
        updateFilter,
        updateFilters,
    } = useFilterPersistence();

    const [showCustomFilterModal, setShowCustomFilterModal] = useState(false);
    const [tempExcludePrefixes, setTempExcludePrefixes] = useState('');
    const [tempKeywordFilter, setTempKeywordFilter] = useState('');
    const [tempHideAutoGenerated, setTempHideAutoGenerated] = useState(true);

    /**
     * Handle filter mode change from SegmentedControl
     */
    const handleViewModeChange = useCallback(({detail}) => {
        const selectedMode = detail.selectedId;

        // If switching to custom filters, open the modal
        if (selectedMode === 'advanced') {
            // Initialize temp values with current persisted values
            setTempExcludePrefixes(excludePrefixes);
            setTempKeywordFilter(keywordFilter);
            
            // Set checkbox based on current mode:
            // - If coming from 'default' (Hide Auto-Generated), checkbox should be checked
            // - If coming from 'all', checkbox should be unchecked
            setTempHideAutoGenerated(viewMode === 'default');
            
            setShowCustomFilterModal(true);
            // Don't update viewMode yet - wait for Apply or Cancel
        } else {
            // Switching to 'all' or 'default' mode
            updateFilters({
                viewMode: selectedMode,
                excludePrefixes: '',
                keywordFilter: '',
                hideAutoGenerated: selectedMode === 'default',
            });
        }
    }, [viewMode, excludePrefixes, keywordFilter, updateFilters]);

    /**
     * Handle custom filter application
     */
    const handleApplyFilters = useCallback(() => {
        const hasExcludeFilters = tempExcludePrefixes && tempExcludePrefixes.trim();
        const hasKeywordFilters = tempKeywordFilter && tempKeywordFilter.trim();
        const hasAnyCustomFilters = hasExcludeFilters || hasKeywordFilters;

        // If user has entered custom filters, stay in advanced mode
        if (hasAnyCustomFilters) {
            updateFilters({
                viewMode: 'advanced',
                excludePrefixes: tempExcludePrefixes,
                keywordFilter: tempKeywordFilter,
                hideAutoGenerated: tempHideAutoGenerated,
            });
        } else {
            // No custom filters entered, switch based on checkbox state
            updateFilters({
                viewMode: tempHideAutoGenerated ? 'default' : 'all',
                excludePrefixes: '',
                keywordFilter: '',
                hideAutoGenerated: tempHideAutoGenerated,
            });
        }
        
        setShowCustomFilterModal(false);
    }, [tempExcludePrefixes, tempKeywordFilter, tempHideAutoGenerated, updateFilters]);

    /**
     * Handle clearing all custom filters
     */
    const handleClearAllFilters = useCallback(() => {
        setTempExcludePrefixes('');
        setTempKeywordFilter('');
        // Don't change the checkbox state when clearing
    }, []);

    /**
     * Handle modal dismissal (Cancel button)
     * When user cancels the modal:
     * - If no custom filters exist, revert based on checkbox state
     * - If custom filters exist, stay in advanced mode
     */
    const handleModalClose = useCallback(() => {
        const hasPersistedExcludeFilters = excludePrefixes && excludePrefixes.trim();
        const hasPersistedKeywordFilters = keywordFilter && keywordFilter.trim();
        const hasAnyPersistedFilters = hasPersistedExcludeFilters || hasPersistedKeywordFilters;

        // If no custom filters were previously saved, revert based on checkbox state
        if (!hasAnyPersistedFilters) {
            updateFilters({
                viewMode: tempHideAutoGenerated ? 'default' : 'all',
                hideAutoGenerated: tempHideAutoGenerated,
            });
        }
        // If custom filters exist, viewMode stays as 'advanced'

        // Discard temporary changes
        setTempExcludePrefixes(excludePrefixes);
        setTempKeywordFilter(keywordFilter);
        setShowCustomFilterModal(false);
    }, [excludePrefixes, keywordFilter, tempHideAutoGenerated, updateFilters]);

    // Don't render if experiments are disabled
    if (!experimentsEnabled) {
        return null;
    }

    return (
        <>
            <SpaceBetween direction="horizontal" size="xs">
                <SegmentedControl
                    selectedId={viewMode}
                    onChange={handleViewModeChange}
                    label="Filtering mode"
                    ariaLabel="Resource filtering mode"
                    options={[
                        {
                            text: 'All',
                            id: 'all',
                            ariaLabel: 'Show all resources without filtering'
                        },
                        {
                            text: 'Hide Auto-Generated',
                            id: 'default',
                            ariaLabel: 'Hide auto-generated resources'
                        },
                        {
                            text: 'Custom Filters',
                            id: 'advanced',
                            ariaLabel: 'Apply custom keyword-based filters'
                        }
                    ]}
                />

                {/* Edit button for custom filters mode */}
                {viewMode === 'advanced' && (
                    <Button
                        variant="icon"
                        iconName="edit"
                        ariaLabel="Edit custom filters"
                        onClick={() => {
                            setTempExcludePrefixes(excludePrefixes);
                            setTempKeywordFilter(keywordFilter);
                            setTempHideAutoGenerated(hideAutoGenerated);
                            setShowCustomFilterModal(true);
                        }}
                    />
                )}
            </SpaceBetween>

            {/* Custom Filters Modal */}
            <CustomFiltersModal
                visible={showCustomFilterModal}
                onDismiss={handleModalClose}
                onApply={handleApplyFilters}
                excludePrefixes={tempExcludePrefixes}
                keywordFilter={tempKeywordFilter}
                hideAutoGenerated={tempHideAutoGenerated}
                onExcludePrefixesChange={setTempExcludePrefixes}
                onKeywordFilterChange={setTempKeywordFilter}
                onHideAutoGeneratedChange={setTempHideAutoGenerated}
                onClearAll={handleClearAllFilters}
            />
        </>
    );
}

export default FilterControlsUI;
